import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { FC } from "react";
import { Link, Outlet, useLocation } from "react-router";

import BrostarIcon from "@/icons/brostar.svg?react";

import { LoginOrBackButton } from "./components";

const NAV_LINKS = [
  { label: "Over ons", to: "/public/over-ons" },
  { label: "Gemeenten", to: "/public/voor-gemeenten" },
  { label: "Waterschappen", to: "/public/voor-waterschappen" },
  { label: "Provincies", to: "/public/voor-provincies" },
  { label: "Drinkwaterbedrijven", to: "/public/voor-drinkwaterbedrijven" },
  { label: "Integraties", to: "/public/integraties" },
  { label: "Aanleveringen", to: "/public/aanleveringen" },
  { label: "Blog", to: "/public/blog" },
];

const NavLink: FC<{ to: string; label: string; active: boolean; onClick?: () => void }> = ({
  to,
  label,
  active,
  onClick,
}) => (
  <Link to={to} onClick={onClick} style={{ textDecoration: "none" }}>
    <Text
      fontWeight="600"
      fontSize="sm"
      color={active ? "primary" : "text"}
      borderBottom={active ? "2px solid" : "2px solid transparent"}
      borderColor={active ? "primary" : "transparent"}
      transition="color 0.15s"
    >
      {label}
    </Text>
  </Link>
);

export const NAVIGATION_HEIGHT = "64px";

export const PublicLayout: FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { pathname } = useLocation();

  return (
    <Box minH="100vh" bg="white">
      {/* Navigation */}
      <Box
        as="nav"
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.100"
        px={{ base: 4, md: 10 }}
        py={3}
        position="sticky"
        top={0}
        zIndex={100}
        boxShadow="sm"
        h={NAVIGATION_HEIGHT}
      >
        <Flex align="center" justify="space-between" maxW="1400px" mx="auto">
          {/* Logo */}
          <Link to="/public" style={{ textDecoration: "none" }}>
            <Flex align="center" gap={2}>
              <BrostarIcon width={28} height={28} />
              <NavLink to="/public" label="BROSTAR" active={pathname === "/public"} />
            </Flex>
          </Link>

          {/* Desktop links */}
          <HStack spacing={5} display={{ base: "none", xl: "flex" }}>
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                label={link.label}
                active={pathname === link.to || pathname.startsWith(link.to + "/")}
              />
            ))}
          </HStack>

          {/* Login + mobile hamburger */}
          <HStack spacing={3}>
            <Link to="/" style={{ textDecoration: "none" }}>
              <LoginOrBackButton />
            </Link>
            <IconButton
              aria-label="Menu openen"
              icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
              variant="ghost"
              display={{ base: "inline-flex", xl: "none" }}
              onClick={isOpen ? onClose : onOpen}
            />
          </HStack>
        </Flex>
      </Box>

      {/* Mobile drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody pt={12}>
            <VStack align="flex-start" spacing={5}>
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  label={link.label}
                  active={pathname === link.to}
                  onClick={onClose}
                />
              ))}
              <Link to="/" onClick={onClose} style={{ textDecoration: "none", width: "100%" }}>
                <Button variant="primary" w="full" mt={4}>
                  <LoginOrBackButton />
                </Button>
              </Link>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Page content */}
      <Box as="main">
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        as="footer"
        bg="accentLight"
        borderTop="1px solid"
        borderColor="gray.200"
        px={{ base: 4, md: 10 }}
        py={10}
        mt={20}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={6}
          maxW="1400px"
          mx="auto"
          wrap="wrap"
        >
          <Link to="/public" style={{ textDecoration: "none" }}>
            <Flex align="center" gap={2}>
              <BrostarIcon width={24} height={24} />
              <Text fontWeight="800" fontSize="sm" color="text">
                BROSTAR
              </Text>
            </Flex>
          </Link>

          <Text fontWeight="400" fontSize="sm" color="gray.600">
            Dé online applicatie voor bronhouders in Nederland.
          </Text>

          <HStack spacing={4} wrap="wrap">
            {NAV_LINKS.map((link) => (
              <Link key={link.to} to={link.to} style={{ textDecoration: "none" }}>
                <Text fontSize="sm" fontWeight="600" color="gray.600">
                  {link.label}
                </Text>
              </Link>
            ))}
          </HStack>

          <Text fontSize="xs" fontWeight="400" color="gray.500">
            © {new Date().getFullYear()} Nelen & Schuurmans. Alle rechten voorbehouden.
          </Text>
        </Flex>
      </Box>
    </Box>
  );
};
